import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { ZodSchema } from "@validation/utils/schemaUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { ColumnSettingsProps, FormGroup, Select, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Permission } from "__generated__/enums";
import { SaveDefaultFrontendTableSettingsDocument } from "__generated__/view/defaultTableSettings/graphql/defaultTableSettings.mutations.generated";

const schema = z.object({
	permission: ZodSchema.requiredField(z.enum(Permission)),
});

type SchemaInput = z.input<typeof schema>;
type SchemaOutput = z.output<typeof schema>;

const TableSaveSettingsDialog = ({
	open,
	onClose,
	tableKey,
	columnSettings,
}: {
	open: boolean;
	onClose: () => void;
	tableKey: string;
	columnSettings: Array<ColumnSettingsProps>;
}) => {
	const { control, handleSubmit, reset } = useForm<SchemaInput, unknown, SchemaOutput>({
		resolver: zodResolver(schema),
		defaultValues: {
			permission: null,
		},
	});

	const [ saveDefaultFrontendTableSetting, { loading } ] = useMutation(SaveDefaultFrontendTableSettingsDocument);

	const handleOnClose = () => {
		onClose();
		reset({
			permission: null,
		});
	};

	const save = (data: SchemaOutput) => {
		saveDefaultFrontendTableSetting({
			variables: {
				permission: data.permission,
				tableKey: tableKey,
				tableSettings: columnSettings,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			handleOnClose();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "DEFAULT_TABLE_SETTINGS" })}
			form='tableSettingsAccountTypeForm'
			loading={loading}>
			<form onSubmit={handleSubmit((form) => save(form))} id='tableSettingsAccountTypeForm'>
				<FormGroup label={intl.formatMessage({ id: "USER_TYPE" })} labelPlacement='left-center' required>
					<Controller
						name={"permission"}
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Select
									{...field}
									fetchOptions={Object.values(Permission).filter(
										(it) => it !== Permission.Administrator,
									)}
									getOptionLabel={option => intl.formatMessage({ id: `USER_TYPE_${option}` })}
									disableSearch
								/>
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)}
					/>
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default TableSaveSettingsDialog;
